import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react"; // Added useRef
import axios from "axios";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  Polyline,
  useJsApiLoader,
} from "@react-google-maps/api"; // Added Polyline
import {
  Search,
  Settings,
  Clock,
  MapPin,
  Power,
  ChevronDown,
  X,
} from "lucide-react";

import "./EmployeeTrackingDashboard.css";
import SettingsServices from "../../services/SettingsServices";
import { useDispatch, useSelector } from "react-redux";
import { setShowMessage } from "../../redux/slices/notificationSlice";
import translations from "../../assets/locals/translations";

// --- Constants ---
const GOOGLE_MAPS_API_KEY = "AIzaSyBrO4YmLsMb1vFBLKUuAHBJgWijXgmdySk";
const DEFAULT_CENTER = { lat: 34.0522, lng: -118.2437 };
const DEFAULT_ZOOM = 4;
const REFRESH_INTERVAL_OPTIONS = [
  { value: 30000, label: "30 seconds" },
  { value: 60000, label: "1 minute" },
  { value: 300000, label: "5 minutes" },
  { value: 600000, label: "10 minutes" },
  { value: 900000, label: "15 minutes" },
];

// --- Mock Data (with locationHistory) ---
const MOCK_EMPLOYEES = [
  {
    id: 1,
    name: "John Doe",
    status: "active",
    lastLatitude: 34.0522,
    lastLongitude: -118.2437,
    lastSeen: new Date().toISOString(),
    avatarUrl: "https://randomuser.me/api/portraits/men/1.jpg",
    locationHistory: [
      {
        latitude: 34.0621,
        longitude: -118.2502,
        timestamp: new Date(Date.now() - 9 * 60000).toISOString(),
      },
      {
        latitude: 34.06,
        longitude: -118.248,
        timestamp: new Date(Date.now() - 8 * 60000).toISOString(),
      },
      {
        latitude: 34.0579,
        longitude: -118.2463,
        timestamp: new Date(Date.now() - 7 * 60000).toISOString(),
      },
      {
        latitude: 34.0555,
        longitude: -118.245,
        timestamp: new Date(Date.now() - 6 * 60000).toISOString(),
      },
      {
        latitude: 34.0538,
        longitude: -118.2445,
        timestamp: new Date(Date.now() - 5 * 60000).toISOString(),
      },
      {
        latitude: 34.0527,
        longitude: -118.2442,
        timestamp: new Date(Date.now() - 4 * 60000).toISOString(),
      },
      {
        latitude: 34.0523,
        longitude: -118.244,
        timestamp: new Date(Date.now() - 3 * 60000).toISOString(),
      },
      {
        latitude: 34.0521,
        longitude: -118.2439,
        timestamp: new Date(Date.now() - 2 * 60000).toISOString(),
      },
      {
        latitude: 34.0522,
        longitude: -118.2438,
        timestamp: new Date(Date.now() - 1 * 60000).toISOString(),
      },
      {
        latitude: 34.0522,
        longitude: -118.2437,
        timestamp: new Date().toISOString(),
      }, // current
    ],
  },
  {
    id: 2,
    name: "Jane Smith",
    status: "inactive",
    lastLatitude: 40.7128,
    lastLongitude: -74.006,
    lastSeen: new Date(Date.now() - 3600000).toISOString(),
    avatarUrl: "https://randomuser.me/api/portraits/women/2.jpg",
    locationHistory: [
      { latitude: 40.7126, longitude: -74.0062 },
      { latitude: 40.7127, longitude: -74.0061 },
      { latitude: 40.7129, longitude: -74.0059 },
      { latitude: 40.713, longitude: -74.006 },
      { latitude: 40.7128, longitude: -74.006 },
    ],
  },
  {
    id: 3,
    name: "Mike Johnson",
    status: "active",
    lastLatitude: 37.7749,
    lastLongitude: -122.4194,
    lastSeen: new Date(Date.now() - 600000).toISOString(),
    avatarUrl: "https://randomuser.me/api/portraits/men/3.jpg",
    locationHistory: [
      { latitude: 37.775, longitude: -122.4192 },
      { latitude: 37.7748, longitude: -122.4195 },
      { latitude: 37.7751, longitude: -122.4193 },
      { latitude: 37.7747, longitude: -122.4196 },
      { latitude: 37.7749, longitude: -122.4194 },
    ],
  },
  {
    id: 4,
    name: "Sarah Williams",
    status: "active",
    lastLatitude: 33.6844,
    lastLongitude: -117.8265,
    lastSeen: new Date().toISOString(),
    avatarUrl: "https://randomuser.me/api/portraits/women/4.jpg",
    locationHistory: [
      { latitude: 33.6843, longitude: -117.8263 },
      { latitude: 33.6845, longitude: -117.8266 },
      { latitude: 33.6846, longitude: -117.8264 },
      { latitude: 33.6842, longitude: -117.8267 },
      { latitude: 33.6844, longitude: -117.8265 },
    ],
  },
];

// --- Helper Components --- (StatusIndicator, EmployeeListItem - same as before)
const StatusIndicator = ({ status }) => {
  let statusClass = "default";
  if (status === "active") statusClass = "active";
  else if (status === "inactive") statusClass = "inactive";
  return <span className={`status-indicator ${statusClass}`}></span>;
};

const EmployeeListItem = ({ employee, onSelect, isSelected }) => (
  <div
    className={`employee-list-item ${isSelected ? "selected" : ""}`}
    onClick={() => onSelect(employee)}
  >
    <img
      src={
        employee.avatarUrl ||
        `https://ui-avatars.com/api/?name=${encodeURIComponent(
          employee.name
        )}&background=random`
      }
      alt={employee.name}
      className="employee-avatar"
    />
    <div className="employee-info">
      <p className="employee-name">{employee.name}</p>
      <p className="employee-meta">
        <StatusIndicator status={employee.status} />
        {employee.status === "active" ? "Online" : "Offline"} - Last seen:{" "}
        {new Date(employee.lastSeen).toLocaleTimeString()}
      </p>
    </div>
  </div>
);

// --- Main Dashboard Component ---
const EmployeeTrackingDashboard = () => {
  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations['en'];

  // Create translated interval options
  const refreshIntervalOptions = [
    { value: 30000, label: t.thirtyseconds || "30 seconds" },
    { value: 60000, label: t.oneminute || "1 minute" },
    { value: 300000, label: t.fiveminutes || "5 minutes" },
    { value: 600000, label: t.tenminutes || "10 minutes" },
    { value: 900000, label: t.fifteenminutes || "15 minutes" },
  ];

  const [employees, setEmployees] = useState([]);
  const dispatch = useDispatch();
  const settings = new SettingsServices();
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);

  const [isTrackingEnabled, setIsTrackingEnabled] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(
    REFRESH_INTERVAL_OPTIONS[1].value
  );

  // State for history path
  const [displayedHistoryEmployeeId, setDisplayedHistoryEmployeeId] =
    useState(null);
  const [historyPath, setHistoryPath] = useState([]);
  const mapRef = useRef(null); // Ref for map instance

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ["places"], // Add 'geometry' if you need advanced geometry functions
  });

  const getUserTracking = async () => {
    try {
      const response = await settings.getUsersTrackingHisotry();
      if (response && response.isSuccess) {
        return response.result || [];
      }
      return [];
    } catch (error) {
      console.error("Error:", error);
      return null;
    }
  };

  const fetchEmployees = useCallback(async () => {
    console.log("Fetching employees from API...");
    setIsLoading(true);

    const realEmployeesData = await getUserTracking();
    console.log("Raw API Data:", realEmployeesData); // Log the raw data

    if (realEmployeesData === null) {
      setIsLoading(false);
      return;
    }

    if (!Array.isArray(realEmployeesData)) {
      console.error("API did not return an array:", realEmployeesData);
      setIsLoading(false);
      setEmployees([]);
      return;
    }

    const transformedEmployees = realEmployeesData.map((apiEmp) => {
      let parsedLocationHistory = [];
      try {
        // Check if locationHistory is a string and needs parsing
        if (
          typeof apiEmp.locationHistory === "string" &&
          apiEmp.locationHistory.trim() !== ""
        ) {
          parsedLocationHistory = JSON.parse(apiEmp.locationHistory);
        } else if (Array.isArray(apiEmp.locationHistory)) {
          parsedLocationHistory = apiEmp.locationHistory; // Already an array
        }
        // Ensure parsedLocationHistory is always an array
        if (!Array.isArray(parsedLocationHistory)) {
          parsedLocationHistory = [];
        }
      } catch (e) {
        console.error(
          `Error parsing locationHistory for employee ${apiEmp.id || apiEmp.name
          }:`,
          e,
          "Original history:",
          apiEmp.locationHistory
        );
        parsedLocationHistory = []; // Default to empty array on parsing error
      }

      // Convert latitude and longitude from strings to numbers for history
      const numericLocationHistory = parsedLocationHistory
        .map((hist) => ({
          latitude: parseFloat(hist.latitude),
          longitude: parseFloat(hist.longitude),
          timestamp: hist.timestamp || new Date().toISOString(), // Ensure timestamp exists
        }))
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)); // Sort by time

      // Convert top-level lat/lng to numbers
      const currentLat = parseFloat(apiEmp.lastLatitude);
      const currentLng = parseFloat(apiEmp.lastLongitude);

      // ---- Option 1: Use API data directly (Recommended for production) ----
      return {
        id: apiEmp.id, // Assuming API 'id' is the correct unique identifier
        name: apiEmp.name || "Unnamed Employee",
        status: apiEmp.status || "inactive", // Default to inactive if status is missing
        lastLatitude: !isNaN(currentLat)
          ? currentLat
          : numericLocationHistory.length > 0
            ? numericLocationHistory[numericLocationHistory.length - 1].latitude
            : DEFAULT_CENTER.lat,
        lastLongitude: !isNaN(currentLng)
          ? currentLng
          : numericLocationHistory.length > 0
            ? numericLocationHistory[numericLocationHistory.length - 1].longitude
            : DEFAULT_CENTER.lng,
        lastSeen:
          apiEmp.lastSeen ||
          (numericLocationHistory.length > 0
            ? numericLocationHistory[numericLocationHistory.length - 1]
              .timestamp
            : new Date().toISOString()),
        avatarUrl: `https://ui-avatars.com/api/?name=${encodeURIComponent(apiEmp.name || "N A")}&background=random` || apiEmp.avatarUrl,
        locationHistory: numericLocationHistory,
        // Include other fields from API if needed, like trackingInterval
        trackingInterval: apiEmp.trackingInterval,
      };
    });

    setEmployees(transformedEmployees);
    setIsLoading(false);
  }, []);

  //   const fetchEmployees = useCallback(async () => {
  //     console.log("Fetching employees...");
  //     // Simulate API call and add/update locationHistory for demo
  //     setIsLoading(true);
  //     const realEmployeesData = await getUserTracking();
  //     console.log(realEmployeesData);
  //     if (realEmployeesData === null) {
  //       setIsLoading(false);
  //       return;
  //     }

  //     if (!Array.isArray(realEmployeesData)) {
  //       console.error("API did not return an array:", realEmployeesData);
  //       setIsLoading(false);
  //       setEmployees([]);
  //       return;
  //     }

  //     setEmployees(
  //       realEmployeesData.map((emp) => {
  //         const newLat = emp.lastLatitude + (Math.random() - 0.5) * 0.001; // smaller changes
  //         const newLng = emp.lastLongitude + (Math.random() - 0.5) * 0.001;
  //         const newHistoryEntry = {
  //           latitude: newLat,
  //           longitude: newLng,
  //           timestamp: new Date().toISOString(),
  //         };
  //         const updatedHistory = emp.locationHistory
  //           ? [...emp.locationHistory.slice(-19), newHistoryEntry]
  //           : [newHistoryEntry]; // Keep last 20 points
  //         return {
  //           ...emp,
  //           lastLatitude: newLat,
  //           lastLongitude: newLng,
  //           lastSeen: new Date().toISOString(),
  //           locationHistory: updatedHistory,
  //         };
  //       })
  //     );
  //   }, []);

  useEffect(() => {
    fetchEmployees();
    let intervalId;
    if (isTrackingEnabled) {
      intervalId = setInterval(fetchEmployees, refreshInterval);
    }
    return () => clearInterval(intervalId);
  }, [fetchEmployees, isTrackingEnabled, refreshInterval]);

  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) =>
      emp.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [employees, searchTerm]);

  const clearHistoryPath = () => {
    setDisplayedHistoryEmployeeId(null);
    setHistoryPath([]);
  };

  const handleSelectEmployee = (employee) => {
    if (
      displayedHistoryEmployeeId !== null &&
      displayedHistoryEmployeeId !== employee.id
    ) {
      clearHistoryPath();
    }
    setSelectedEmployee(employee);
    setActiveMarker(employee.id);
    if (employee.lastLatitude && employee.lastLongitude) {
      setMapCenter({ lat: employee.lastLatitude, lng: employee.lastLongitude });
      // Don't zoom in too much if history is shown, let fitBounds handle it
      if (displayedHistoryEmployeeId !== employee.id) {
        // Only zoom if not showing history for this one already
        setMapZoom(14);
      }
    }
  };

  const handleMarkerClick = (employee) => {
    if (
      displayedHistoryEmployeeId !== null &&
      displayedHistoryEmployeeId !== employee.id
    ) {
      clearHistoryPath();
    }
    setSelectedEmployee(employee); // Also select in sidebar
    setActiveMarker(employee.id);
    if (employee.lastLatitude && employee.lastLongitude) {
      setMapCenter({ lat: employee.lastLatitude, lng: employee.lastLongitude });
    }
  };

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onMapUnmount = useCallback((map) => {
    mapRef.current = null;
  }, []);

  const handleToggleHistoryDisplay = (employee) => {
    if (displayedHistoryEmployeeId === employee.id) {
      // Hide history
      clearHistoryPath();
      // Optionally reset zoom to employee's current location
      if (employee.lastLatitude && employee.lastLongitude) {
        setMapCenter({
          lat: employee.lastLatitude,
          lng: employee.lastLongitude,
        });
        setMapZoom(14);
      }
    } else {
      // Show history
      if (employee.locationHistory && employee.locationHistory.length > 0) {
        const path = employee.locationHistory.map((loc) => ({
          lat: loc.latitude,
          lng: loc.longitude,
        }));
        setHistoryPath(path);
        setDisplayedHistoryEmployeeId(employee.id);

        // Fit map to bounds of the history path
        if (mapRef.current && window.google && window.google.maps) {
          const bounds = new window.google.maps.LatLngBounds();
          path.forEach((point) => bounds.extend(point));
          // Also include current marker if not in path
          bounds.extend({
            lat: employee.lastLatitude,
            lng: employee.lastLongitude,
          });
          mapRef.current.fitBounds(bounds);
          // Add a little padding after fitBounds
          const listener = window.google.maps.event.addListenerOnce(
            mapRef.current,
            "idle",
            () => {
              if (mapRef.current.getZoom() > 16) mapRef.current.setZoom(16); // Don't zoom too far
            }
          );
          // setTimeout(() => window.google.maps.event.removeListener(listener), 2000); // Clean up listener
        }
      } else {
        clearHistoryPath(); // No history to show
        alert(`${employee.name} has no location history.`);
      }
    }
  };

  const handleInfoWindowClose = () => {
    // If the InfoWindow being closed was for the employee whose history is shown
    if (activeMarker === displayedHistoryEmployeeId) {
      // Optionally keep history, or clear it. Let's keep it for now unless explicitly cleared.
      // clearHistoryPath(); // Uncomment to clear history when InfoWindow closes
    }
    setActiveMarker(null);
  };

  const handleTracking = async ({ status, interval, u_id }) => {
    if (!u_id) {
      dispatch(setShowMessage("Please Select employee."));
      return;
    }
    try {
      const response = await settings.userTracking({
        status,
        interval: interval / 60000, // Convert ms to minutes
        u_id,
      });
      if (response && response.isSuccess) {
        console.log("Tracking settings updated successfully");
      }
    } catch (error) {
      console.error("Error in handleTracking:", error);
    }
  };

  const handleToggleTracking = async () => {
    if (!activeMarker) {
      dispatch(setShowMessage("Please Select employee."));
      return;
    }
    const newStatus = !isTrackingEnabled;
    setIsTrackingEnabled(newStatus);
    await handleTracking({
      status: newStatus,
      interval: refreshInterval,
      u_id: activeMarker,
    });
  };

  const handleChangeInterval = async (e) => {
    if (!activeMarker) {
      dispatch(setShowMessage("Please Select employee."));
      return;
    }
    const newInterval = parseInt(e.target.value, 10);
    setRefreshInterval(newInterval);
    await handleTracking({
      status: isTrackingEnabled,
      interval: newInterval,
      u_id: activeMarker,
    });
  };

  const mapContainerStyle = useMemo(
    () => ({ width: "100%", height: "100%" }),
    []
  );

  if (loadError)
    return (
      <div className="error-message">
        Error loading maps: {loadError.message}
      </div>
    );
  if (!isLoaded) return <div className="loading-message">Loading Maps...</div>;

  return (
    <div className="tracking-dashboard-container">
      {/* --- Sidebar (same as before) --- */}
      <div className="sidebar">
        <div className="sidebar-header">
          <h1 className="sidebar-title">
            <MapPin /> {t.employeetracking || "Employee Tracking"}
          </h1>
        </div>

        <div className="search-bar-container">
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder={t.searchemployees || "Search employees..."}
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="search-icon" />
          </div>
        </div>

        <div className="employee-list-container">
          {filteredEmployees.length > 0 ? (
            filteredEmployees.map((emp) => (
              <EmployeeListItem
                key={emp.id}
                employee={emp}
                onSelect={handleSelectEmployee}
                isSelected={selectedEmployee?.id === emp.id}
              />
            ))
          ) : (
            <p className="no-employees-message">{t.noemployeesfound || "No employees found."}</p>
          )}
        </div>

        <div className="settings-panel">
          <h2 className="settings-title">
            <Settings /> {t.trackingsettings || "Tracking Settings"}
          </h2>
          <div className="settings-item">
            <label htmlFor="trackingToggle" className="settings-label">
              <Power
                className={`power-icon ${isTrackingEnabled ? "active" : "inactive"
                  }`}
              />
              {t.livetracking || "Live Tracking"}
            </label>
            <button
              id="trackingToggle"
              onClick={handleToggleTracking}
              className={`tracking-toggle-button ${isTrackingEnabled ? "on" : "off"
                }`}
            >
              {isTrackingEnabled ? (t.on || "ON") : (t.off || "OFF")}
            </button>
          </div>
          <div className="settings-item">
            <label htmlFor="refreshInterval" className="settings-label">
              <Clock className="clock-icon" />
              {t.updateinterval || "Update Interval"}
            </label>
            <div className="interval-select-wrapper">
              <select
                id="refreshInterval"
                value={refreshInterval}
                onChange={handleChangeInterval}
                className="interval-select"
                disabled={!isTrackingEnabled}
              >
                {refreshIntervalOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <div className="select-arrow">
                <ChevronDown />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- Map Panel --- */}
      <div className="map-panel">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={mapZoom}
          onLoad={onMapLoad} // Add onLoad
          onUnmount={onMapUnmount} // Add onUnmount
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: true,
            zoomControlOptions: {
              position: window.google?.maps?.ControlPosition?.RIGHT_TOP,
            },
            gestureHandling: "greedy",
          }}
        >
          {/* Employee Markers */}
          {employees.map(
            (emp) =>
              emp.lastLatitude &&
              emp.lastLongitude && (
                <Marker
                  key={emp.id}
                  position={{ lat: emp.lastLatitude, lng: emp.lastLongitude }}
                  onClick={() => handleMarkerClick(emp)}
                  icon={{
                    url:
                      emp.avatarUrl ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        emp.name.charAt(0)
                      )}&background=0D8ABC&color=fff&size=40&rounded=true&font-size=0.5`,
                    scaledSize: new window.google.maps.Size(40, 40),
                    anchor: new window.google.maps.Point(20, 20),
                  }}
                  zIndex={
                    activeMarker === emp.id ||
                      displayedHistoryEmployeeId === emp.id
                      ? 100
                      : 1
                  } // Bring active/history marker to front
                >
                  {activeMarker === emp.id && (
                    <InfoWindow
                      position={{
                        lat: emp.lastLatitude,
                        lng: emp.lastLongitude,
                      }}
                      onCloseClick={handleInfoWindowClose}
                    >
                      <div className="info-window-content">
                        <div className="info-window-header">
                          <img
                            src={
                              emp.avatarUrl ||
                              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                emp.name
                              )}&background=random`
                            }
                            alt={emp.name}
                            className="info-window-avatar"
                          />
                          <div>
                            <h3 className="info-window-name">{emp.name}</h3>
                            <p
                              className={`info-window-status ${emp.status === "active" ? "active" : "inactive"
                                }`}
                            >
                              {emp.status === "active" ? "Online" : "Offline"}
                            </p>
                          </div>
                        </div>
                        <p className="info-window-detail">
                          <strong>Last Seen:</strong>{" "}
                          {new Date(emp.lastSeen).toLocaleString()}
                        </p>
                        <p className="info-window-detail">
                          <strong>Coords:</strong> {emp.lastLatitude.toFixed(4)}
                          , {emp.lastLongitude.toFixed(4)}
                        </p>
                        {emp.locationHistory &&
                          emp.locationHistory.length > 0 && (
                            <button
                              className="info-window-button"
                              onClick={() => handleToggleHistoryDisplay(emp)}
                            >
                              {displayedHistoryEmployeeId === emp.id
                                ? "Hide History"
                                : "View History"}
                            </button>
                          )}
                      </div>
                    </InfoWindow>
                  )}
                </Marker>
              )
          )}

          {/* History Path Polyline */}
          {historyPath.length > 0 && displayedHistoryEmployeeId !== null && (
            <Polyline
              path={historyPath}
              options={{
                strokeColor: "#FF0000", // Red
                strokeOpacity: 0.7,
                strokeWeight: 4,
                geodesic: true, // Draws line following Earth's curvature
                icons: [
                  {
                    // Arrow icons along the path
                    icon: {
                      path: window.google?.maps?.SymbolPath
                        .FORWARD_CLOSED_ARROW,
                      scale: 3, // Size of the arrow
                      strokeColor: "#FF0000", // Arrow color
                      fillColor: "#FF0000", // Arrow fill color
                      fillOpacity: 1,
                    },
                    offset: "0", // Start placing arrows
                    repeat: "75px", // Repeat every 75 pixels along the line
                  },
                ],
              }}
            />
          )}
          {/* Optional: Markers for each history point if desired */}
          {historyPath.length > 0 &&
            displayedHistoryEmployeeId !== null &&
            historyPath.map((point, index) => (
              <Marker
                key={`history-${displayedHistoryEmployeeId}-${index}`}
                position={point}
                icon={{
                  path: window.google?.maps?.SymbolPath.CIRCLE,
                  scale: 3, // Small dot
                  fillColor: "#FF0000",
                  fillOpacity: 0.7,
                  strokeWeight: 0,
                }}
                title={
                  employees.find((e) => e.id === displayedHistoryEmployeeId)
                    ?.locationHistory[index]?.timestamp
                    ? `Point ${index + 1} at ${new Date(
                      employees.find(
                        (e) => e.id === displayedHistoryEmployeeId
                      )?.locationHistory[index]?.timestamp
                    ).toLocaleTimeString()}`
                    : `Point ${index + 1}`
                } // Add timestamp if available
              />
            ))}
        </GoogleMap>
        {selectedEmployee && (
          <button
            onClick={() => {
              setSelectedEmployee(null);
              setActiveMarker(null);
              clearHistoryPath(); // Also clear history path on general reset
              setMapCenter(DEFAULT_CENTER);
              setMapZoom(DEFAULT_ZOOM);
            }}
            className="clear-selection-button"
            title="Clear selection & reset view"
          >
            <X />
          </button>
        )}
      </div>
    </div>
  );
};

export default EmployeeTrackingDashboard;
